import React from "react";
import { Reveal } from "../components/Reveal";
import { Quote, Users, Sprout, Handshake, Leaf, ShieldCheck } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import SEO, { organizationSchema } from "../components/SEO";

export default function About() {
  const { t } = useLang();
  const vicons = [Sprout, Users, Handshake, ShieldCheck, Leaf];

  return (
    <div>
      <SEO metaKey="about" pathname="/about" schemas={[organizationSchema()]} />

      {/* Hero with image + dark overlay */}
      <section className="relative isolate bg-forest text-white">
        <div className="absolute inset-0 z-0">
          <img src="/images/gallery/lsp-10.jpg" alt="" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-black/55 md:bg-forest/75" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-28 lg:py-40 min-h-[70vh] flex flex-col justify-end">
          <Reveal>
            <div className="editorial-divider mb-6 max-w-xs" style={{ color: "#E3A839" }}>{t("about_page.eyebrow")}</div>
            <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-8xl leading-[0.96] text-white max-w-5xl">
              {t("about_page.h1a")} <br /> <span className="italic text-mustard">{t("about_page.h1b")}</span>
            </h1>
            <p className="mt-10 max-w-2xl text-lg text-white/85 leading-relaxed">{t("about_page.intro")}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory">
        <div className="max-w-4xl mx-auto px-5 py-20 lg:py-28 text-center">
          <Reveal>
            <Quote className="w-10 h-10 mx-auto text-terracotta mb-6" />
            <p className="font-serif italic text-3xl lg:text-5xl text-forest leading-[1.15]">{t("about_page.quote")}</p>
            <div className="mt-6 text-[12px] tracking-[0.3em] uppercase text-clay">{t("about_page.quote_by")}</div>
          </Reveal>
        </div>
      </section>

      <section className="bg-forest text-ivory">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <div className="editorial-divider mb-5" style={{ color: "#E3A839" }}>{t("about_page.vision_eyebrow")}</div>
            <h2 className="font-serif text-3xl lg:text-5xl leading-[1.08]">
              <span className="italic text-mustard">{t("about_page.vision_text")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="editorial-divider mb-5" style={{ color: "#E3A839" }}>{t("about_page.mission_eyebrow")}</div>
            <ul className="space-y-5 text-ivory/85 text-lg">
              {["m1","m2","m3","m4","m5","m6"].map((k, i) => (
                <li key={k} className="flex gap-4 items-start border-b border-ivory/10 pb-5">
                  <span className="font-serif text-mustard text-2xl leading-none">{String(i+1).padStart(2,'0')}</span>
                  <span>{t(`about_page.${k}`)}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="kraft-texture">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
          <Reveal>
            <div className="editorial-divider mb-5">{t("about_page.values_eyebrow")}</div>
            <h2 className="font-serif text-4xl lg:text-6xl text-forest max-w-2xl">{t("about_page.values_h2")}</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {["v1","v2","v3","v4","v5"].map((k, i) => {
              const Icon = vicons[i];
              return (
                <Reveal key={k} delay={i*0.08}>
                  <div className="p-8 border border-dust bg-ivory/60 rounded-xl h-full hover:-translate-y-1 transition-transform duration-500">
                    <div className="w-11 h-11 rounded-full bg-forest text-ivory flex items-center justify-center mb-5"><Icon className="w-5 h-5" /></div>
                    <h3 className="font-serif text-2xl text-forest">{t(`about_page.${k}_title`)}</h3>
                    <p className="mt-3 text-ink/70 leading-relaxed">{t(`about_page.${k}_text`)}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-ivory">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <img src="/images/gallery/lsp-11.jpg" alt="LSP farmer groups" className="w-full h-[440px] object-cover rounded-xl" loading="lazy" />
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="editorial-divider mb-5">{t("about_page.serves_eyebrow")}</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-forest leading-[1.05]">{t("about_page.serves_h2")}</h2>
            <div className="mt-8 flex flex-wrap gap-3">
              {(t("about_page.serves") || []).map((s) => (
                <span key={s} className="px-5 py-3 rounded-full border border-forest/20 bg-kraft/40 text-forest">{s}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
